import { useEffect, useState } from 'react';
import { useEmployee } from './EmployeeContext';
import { useModal } from '../../components/ModalProvider';
import { useEnsureOnboarding } from './onboarding/useEnsureOnboarding';
import { employeeApi } from '../../lib/api';
import { PUNCH_TYPES, labelOf } from '../../lib/constants';
import { todayStr, nowISO, fmtHM } from '../../lib/format';
import { capturePhoto, deviceMeta, tryGetIP, getCurrentPosition, geoPreCheckMessage, geoErrorMessage, urlToDataUrl } from '../../lib/photo';
import { generateReceiptPDF } from '../../lib/pdf';
import { ErrorModalBody } from '../../components/ErrorModalBody';
import { computeHorarioForTipo } from '../../lib/schedule';
import type { PunchKey } from '../../lib/types';

type PontoState = Record<string, any>;

export function PontoTab() {
  const { employee, toleranciaMin, tipoHorario, turnoDiurno, turnoNoturno } = useEmployee();
  const modoTurno = tipoHorario === 'turno' && !!employee.turno;
  const turnoCfg = employee.turno === 'diurno' ? turnoDiurno : turnoNoturno;

  function horarioTurno(typeKey: PunchKey): string | null {
    if (!modoTurno || !turnoCfg?.entrada) return null;
    return computeHorarioForTipo(employee.turno, turnoCfg.entrada, typeKey);
  }

  const { showModal, closeModal } = useModal();
  const runGated = useEnsureOnboarding();
  const [rec, setRec] = useState<PontoState>({});
  const [loading, setLoading] = useState(true);
  const date = todayStr();

  async function reload() {
    const res = await employeeApi.getPonto(date);
    setRec(res.record || {});
  }
  useEffect(() => { setLoading(true); reload().finally(() => setLoading(false)); }, []);

  const pending = PUNCH_TYPES.find((p) => !rec[p.key]);

  function attemptPunch(typeKey: PunchKey) {
    runGated(() => doAttemptPunch(typeKey));
  }

  function doAttemptPunch(typeKey: PunchKey) {
    if (modoTurno) {
      // Modo por turno: sem tolerância/justificativa — o horário oficial é
      // calculado a partir do turno do funcionário, não do clique real.
      proceedGeo(typeKey, false, '');
      return;
    }
    const horario = employee.horarios ? employee.horarios[typeKey] : null;
    if (horario) {
      const [h, m] = horario.split(':').map(Number);
      const exp = new Date(date + 'T00:00:00');
      exp.setHours(h, m, 0, 0);
      const diffMin = (Date.now() - exp.getTime()) / 60000;
      if (Math.abs(diffMin) > toleranciaMin) {
        showJustificationModal(typeKey, horario, diffMin);
        return;
      }
    }
    proceedGeo(typeKey, false, '');
  }

  function showJustificationModal(typeKey: PunchKey, horario: string, diffMin: number) {
    const sign = diffMin > 0 ? 'depois' : 'antes';
    let justificativa = '';
    const Body = () => {
      const [val, setVal] = useState('');
      const [err, setErr] = useState('');
      return (
        <>
          <h2 style={{ color: 'var(--coral)' }}>Fora do horário previsto</h2>
          <p className="muted">Você está batendo <b>{labelOf(typeKey)}</b> {Math.round(Math.abs(diffMin))} min {sign} do horário combinado ({horario}). O registro será feito normalmente, mas pedimos uma justificativa curta.</p>
          <label>Justificativa</label>
          <input type="text" placeholder="Ex: fila no acesso, atraso do transporte..." value={val} onChange={(e) => { setVal(e.target.value); justificativa = e.target.value; }} />
          {err && <div className="err">{err}</div>}
          <div className="row" style={{ marginTop: 14 }}>
            <button className="btn btn-ghost" style={{ flex: 1 }} onClick={closeModal}>Cancelar</button>
            <button className="btn btn-primary" style={{ flex: 1 }} onClick={() => {
              if (!val.trim()) { setErr('Escreva uma justificativa antes de continuar.'); return; }
              proceedGeo(typeKey, true, val.trim());
            }}>Confirmar registro</button>
          </div>
        </>
      );
    };
    showModal(<Body />);
  }

  function proceedGeo(typeKey: PunchKey, foraDoEsperado: boolean, justificativa: string) {
    const geoMsg = geoPreCheckMessage();
    if (geoMsg) {
      showModal(<><h2 style={{ color: 'var(--coral)' }}>Localização indisponível</h2><p className="muted">{geoMsg}</p><button className="btn btn-ghost btn-block" onClick={closeModal}>Entendi</button></>);
      return;
    }
    showModal(<div style={{ textAlign: 'center', padding: '20px 0' }}><p className="muted">Obtendo localização…</p></div>);
    getCurrentPosition().then((pos) => {
      finishWithSelfie(typeKey, foraDoEsperado, justificativa, pos.coords.latitude, pos.coords.longitude, Math.round(pos.coords.accuracy));
    }).catch((err) => {
      showModal(<>
        <h2 style={{ color: 'var(--coral)' }}>Não foi possível obter a localização</h2>
        <p className="muted">{geoErrorMessage(err)}</p>
        <button className="btn btn-primary btn-block" onClick={() => proceedGeo(typeKey, foraDoEsperado, justificativa)}>Tentar novamente</button>
        <button className="btn btn-ghost btn-block" style={{ marginTop: 8 }} onClick={closeModal}>Fechar</button>
      </>);
    });
  }

  function finishWithSelfie(typeKey: PunchKey, foraDoEsperado: boolean, justificativa: string, lat: number, lng: number, accuracy: number) {
    showModal(<div style={{ textAlign: 'center', padding: '16px 0' }}>
      <p className="muted">Localização confirmada. Agora tire uma selfie para confirmar sua identidade — é obrigatória para concluir o registro.</p>
      <button className="btn btn-primary btn-block" onClick={async () => {
        try {
          showModal(<div style={{ textAlign: 'center', padding: '20px 0' }}><p className="muted">Abrindo câmera…</p></div>);
          const nowLabel = new Date().toLocaleString('pt-BR');
          const meta = [employee.nome, `${labelOf(typeKey)} — ${nowLabel}`, `Lat ${lat.toFixed(5)}, Lng ${lng.toFixed(5)}`];
          const photo = await capturePhoto('user', meta);
          if (!photo) {
            showModal(<><h2 style={{ color: 'var(--coral)' }}>Foto obrigatória</h2><p className="muted">Não foi possível registrar o ponto sem a selfie de confirmação. Tente novamente.</p><button className="btn btn-ghost btn-block" onClick={closeModal}>Fechar</button></>);
            return;
          }
          const ip = await tryGetIP();
          const res = await employeeApi.punch({
            tipo: typeKey, time: nowISO(), lat, lng, accuracy, foto: photo, ip, device: deviceMeta(), foraDoEsperado, justificativa,
          }) as { record: any };
          showModal(<div style={{ textAlign: 'center' }}>
            <div className="stamp-anim"><b>{labelOf(typeKey)}</b><span className="t">{fmtHM(res.record.time)}</span></div>
            <p className="muted">Ponto registrado com localização, selfie e cadeia de verificação.</p>
            <button className="btn btn-primary btn-block" onClick={closeModal}>Ok</button>
          </div>);
          await reload();
        } catch (err) {
          showModal(<ErrorModalBody error={err} onClose={closeModal} onRetry={() => finishWithSelfie(typeKey, foraDoEsperado, justificativa, lat, lng, accuracy)} />);
        }
      }}>Tirar selfie</button>
      <button className="btn btn-ghost btn-block" style={{ marginTop: 8 }} onClick={closeModal}>Cancelar</button>
    </div>);
  }

  async function downloadPDF(typeKey: PunchKey) {
    const entry = rec[typeKey];
    if (!entry) return;
    const foto = entry.fotoUrl ? await urlToDataUrl(entry.fotoUrl) : null;
    generateReceiptPDF(`Comprovante de Ponto — ${labelOf(typeKey)}`, employee, {
      Data: date.split('-').reverse().join('/'),
      Hora: fmtHM(entry.time),
      Localização: `${entry.lat}, ${entry.lng} (precisão ${entry.accuracy}m)`,
      ...(entry.foraDoEsperado ? { 'Fora do esperado': entry.justificativa || '—' } : {}),
      'Verificação em cadeia': (entry.hashAnterior || '').slice(0, 20) + '…',
    }, foto, entry.hash);
  }

  if (loading) return <div className="card"><p className="muted">Carregando…</p></div>;

  return (
    <div className="card">
      <h2>Ponto de hoje</h2>
      <p className="hint">{new Date().toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: 'long' })}</p>
      <div className="punch-grid">
        {PUNCH_TYPES.map((p) => {
          const done = rec[p.key];
          const isNext = pending && pending.key === p.key;
          return (
            <div key={p.key} className={`punch-card ${done ? (done.foraDoEsperado ? 'flagged' : 'done') : ''}`}>
              <div className="lbl">{p.label}</div>
              {done ? (
                <>
                  <div className="time">{fmtHM(done.time)}</div>
                  <div className={`pill ${done.foraDoEsperado ? 'pill-warn' : 'pill-ok'}`}>{done.foraDoEsperado ? '⚠ fora do esperado' : '✓ registrado'}</div>
                  <button className="btn btn-ghost btn-sm" style={{ marginTop: 8, width: '100%' }} onClick={() => downloadPDF(p.key)}>Baixar comprovante</button>
                </>
              ) : (
                <>
                  <div className="time mono">--:--</div>
                  <div className="exp">{horarioTurno(p.key) ? 'confirmar ' + horarioTurno(p.key) : (employee.horarios?.[p.key] ? 'previsto ' + employee.horarios[p.key] : '')}</div>
                  <button className={`btn ${isNext ? 'btn-primary' : 'btn-ghost'} btn-sm`} style={{ marginTop: 8, width: '100%' }} disabled={!isNext} onClick={() => attemptPunch(p.key)}>
                    {isNext ? (horarioTurno(p.key) ? `Confirmar ${horarioTurno(p.key)}` : 'Bater agora') : 'Aguardando'}
                  </button>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
